import APIs from '../../../api'
import { apiClient } from '../../../api/client'
import { listUnattachedDevices } from '../Devices/api'
import { listUnattachedExternalModules } from '../externalModules/api'
import { listUnattachedInputModules } from '../inputModules/api'

// GET /asset-tags — backend accepts page + limit + search + couplingStatus
// + orderBy + order. PaginationFooter ships `per_page` + `current_page`
// (and other keys); remap to the API's shape and forward the toolbar
// search and the V-Tags modal's couplingStatus so the filters actually
// filter server-side. `orderBy` / `order` are forwarded verbatim so the
// sortable columns (`assetTagSerial`, `couplingStatus`, …) hit the backend
// unchanged.
const listAssetTags = (params = {}) => {
  const { per_page, current_page, search, orderBy, order, filterFields, filterInputs } =
    params || {}
  const query = { page: current_page, limit: per_page }
  if (search) query.search = search
  if (orderBy) query.orderBy = orderBy
  if (order) query.order = order
  filterFields?.forEach((field, index) => {
    const value = filterInputs?.[index]
    if (value != null && value !== '') {
      query[field] = value
    }
  })
  return apiClient.get(APIs.ASSET_TAGS.list, { params: query })
}

// in `{ success, message, data }`; the V-Tag object lives at `data`.
const getAssetTagByUuid = (uuid) => apiClient.get(APIs.ASSET_TAGS.byUuid(uuid))

// POST /asset-tags/decouple-device — detach the currently coupled device
// from an asset tag. Body shape matches the backend's expected payload:
// `{ assetTagId }`. Response body follows the same `{ success, message, data }`
// envelope as the rest of the asset-tags endpoints.
const decoupleAssetTag = (assetTagId) => apiClient.put(APIs.ASSET_TAGS.decouple, { assetTagId })

// PUT /asset-tags/couple-device — attach a device to an asset tag or
// swap an existing coupling. Body shape matches the backend's expected
// payload: `{ deviceId, assetTagId, status, reasonNotes }`. `status`
// distinguishes the flows:
//   - "COUPLE"      → first-time coupling of an empty V-Tag
//   - "SWAP"        → swapping the coupled device on a V-Tag
//   - "REPLACEMENT" → replacing the coupled device (typically inactive)
// `reasonNotes` is optional and only forwarded when present so the
// backend doesn't receive a noisy empty string for the common case.
const coupleAssetTag = ({ deviceId, assetTagId, status, reasonNotes }) => {
  const body = { deviceId, assetTagId, status }
  if (reasonNotes) body.reasonNotes = reasonNotes
  return apiClient.put(APIs.ASSET_TAGS.couple, body)
}

// Devices list unwrapped for `CustomAutoSelect.loadPage`, which expects
// `Array.isArray(res?.data)` to find the items array — so we return the
// backend body (`response.data`) rather than the array itself. The picker
// hits the dedicated `/devices/unattached` endpoint (instead of `/devices`)
// so only devices not currently coupled to any V-Tag are returned. The
// optional `manufacturerId` is forwarded so callers can pre-filter the
// dropdown (e.g. when swapping a V-Tag's device we want to show only
// devices from the same manufacturer). The optional `status` is forwarded
// so callers can pre-filter by device status — the couple/swap modal uses
// `status='ACTIVE'` to hide retired devices.
const fetchDevicesForDropdown = async (opts, manufacturerId, status) => {
  const { page, limit, search } = opts || {}
  const response = await listUnattachedDevices({
    current_page: page,
    per_page: limit,
    search,
    manufacturerId,
    status,
  })
  return response.data
}

// Stable id extractor for CustomAutoSelect — falls back through
// `id` → `_id` so selection tracking survives backend shape changes.
const pickOptionValue = (opt) => opt?.id ?? opt?._id ?? ''

// External-modules list unwrapped for `CustomAutoSelect.loadPage`, which
// expects `Array.isArray(res?.data)` to find the items array — so we
// return the backend body (`response.data`) rather than the array itself.
// The picker hits the dedicated `/external-modules/unattached` endpoint
// (instead of `/external-modules`) so only external modules not currently
// coupled to any V-Tag are returned. The sort is fixed to
// `orderBy=createdAt&order=DESC` so the picker mirrors the documented
// curl ordering and shows the most recently registered modules first;
// CustomAutoSelect is a search/load widget, not a sortable column, so
// keeping the order fixed matches how the device picker behaves. Used
// by the couple/swap modal when the V-Tag's `type` is EXTERNAL_MODULE
// so the user can only pick a free external module for the new coupling.
const fetchExternalModulesForDropdown = async (opts) => {
  const { page, limit, search } = opts || {}
  const response = await listUnattachedExternalModules({
    per_page: limit,
    current_page: page,
    search,
    orderBy: 'createdAt',
    order: 'DESC',
  })
  return response.data
}

// Input-modules list unwrapped for `CustomAutoSelect.loadPage` — same
// unwrap contract + default sort as `fetchExternalModulesForDropdown`.
// Hits the dedicated `/input-modules/unattached` endpoint so the
// couple/swap modal lists only input modules that aren't currently
// bound to a V-Tag when the user picks INPUT_MODULE as the V-Tag's
// `type`.
const fetchInputModulesForDropdown = async (opts) => {
  const { page, limit, search } = opts || {}
  const response = await listUnattachedInputModules({
    per_page: limit,
    current_page: page,
    search,
    orderBy: 'createdAt',
    order: 'DESC',
  })
  return response.data
}

// GET /asset-tags/export — streams a CSV of the filtered V-Tag set.
// Backed by the same backend DTO contract as `listAssetTags` (search,
// couplingStatus) but ignores `page`/`limit`. `columns` is an array;
// axios serializes it as repeated `columns=…&columns=…` query params to
// match the documented curl. `responseType: 'blob'` keeps the CSV payload
// opaque so axios doesn't try to JSON-parse a `text/csv` body.
const exportAssetTagsCsv = async ({ columns, orderBy, order, ...filters } = {}) => {
  const params = {}
  if (columns?.length) params.columns = columns
  if (orderBy) params.orderBy = orderBy
  if (order) params.order = order
  // Forward every remaining filter key as-is. Empty strings are dropped
  // server-side, so sending `''` for unset filters is harmless — but we
  // strip them here to keep the URL clean.
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== '' && v != null) params[k] = v
  })
  const response = await apiClient.get(APIs.ASSET_TAGS.export, {
    params,
    responseType: 'blob',
  })
  return response.data
}

export {
  listAssetTags,
  getAssetTagByUuid,
  decoupleAssetTag,
  coupleAssetTag,
  fetchDevicesForDropdown,
  fetchExternalModulesForDropdown,
  fetchInputModulesForDropdown,
  exportAssetTagsCsv,
  pickOptionValue,
}
